import { styled } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";
import { Masked } from "work/masked-fields";
import { HeadControls } from "work/_views/work-page-styled-components";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import {
  ArchivedControlWithHistory,
  ArchivedFieldHistoryButton,
} from "work/proposals/_views/archive/archived-field-history";
import VendorTeamReadOnly from "work/work-agreements/teams/vendor-team/_views/vendor-team-readonly-view";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";

const ListContainer = styled(Grid)(() => ({
  height: "100%",
  minHeight: "20rem",
}));
/**
 * The archived roster, flush to the pane's edge. The tab's row runs flush so its tables can meet the
 * edges — the fee schedule's templates table does the same.
 */
const TeamReview = styled(VendorTeamReadOnly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
}));

type TeamTabProps = {
  entityId: Guid;
  proposal: Proposal;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const { proposal } = props;

  const individualUserIds: Set<string> = useMemo(() => {
    const vendorTeam = proposal.vendorTeam;
    if (Masked.isMasked(vendorTeam) || !vendorTeam?.leader) return new Set<string>();

    return new Set([
      vendorTeam.leader.userId.value,
      ...vendorTeam.members.map((member) => member.userId.value),
    ]);
  }, [proposal.vendorTeam]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: [...individualUserIds] } : skipToken,
  );

  const individuals = useMemo(() => {
    if (!individualQuery.data) return [];
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const leader = individuals.find(
    (ind: Individual) =>
      Masked.isPresentNotMasked(proposal.vendorTeam) && ind.userId.isEqualTo(proposal.vendorTeam.leader?.userId),
  );

  const members = individuals.filter(
    (ind: Individual) =>
      Masked.isNotMasked(proposal.vendorTeam) && !ind.userId.isEqualTo(proposal.vendorTeam?.leader?.userId),
  );

  if (Masked.isMasked(proposal.vendorTeam) || Masked.isMasked(proposal.teamRestricted)) {
    return maskedProposalFieldNotice(MaskedProposalField.Team);
  }

  return (
    <>
      <HeadControls>
        <ArchivedControlWithHistory field={ProposalField.TeamRestriction} toolTip="View team restriction change history">
          <FormControlLabel
            checked={proposal.teamRestricted.value}
            control={<Switch disabled={true} color="primary" />}
            label={proposal.teamRestricted.value ? <strong>Team Restricted</strong> : "Team Not Restricted"}
          />
        </ArchivedControlWithHistory>
        {/* The whole team's history — its leader and every member who came and went. */}
        <ArchivedFieldHistoryButton field={ProposalField.Team} toolTip="View team change history" />
      </HeadControls>
      <ListContainer container direction="row">
        <Grid size="grow">
          <TeamReview leader={leader ?? null} members={members} />
        </Grid>
      </ListContainer>
    </>
  );
}
